import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiGetGlossary, apiAddGlossaryEntry, apiUpdateGlossaryEntry, apiDeleteGlossaryEntry, GLOSSARY_CATEGORIES } from "./api";
import GlossaryEntryModal from "./GlossaryEntryModal";
import { openGlossaryPrint } from "./glossaryPrint";
import { CustomIcon } from "../../shared/CustomIcon";

// ============================================================
// GlossaryTab — bilingual French/English reference for the
// Translation department. Leader can add / edit / delete, team
// members read-only. Everyone can download the PDF via the
// browser print dialog.
// ============================================================

const SORT_OPTIONS = [
  { key: "french", label: "French (A-Z)" },
  { key: "english", label: "English (A-Z)" },
  { key: "category", label: "Category" },
];

export default function GlossaryTab({ deptId, canEdit }) {
  const [gloss, setGloss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortKey, setSortKey] = useState("french");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const g = await apiGetGlossary(deptId);
      setGloss(g);
    } catch (e) {
      setError(e?.message || "Could not load glossary");
    } finally {
      setLoading(false);
    }
  }, [deptId]);

  useEffect(() => { reload(); }, [reload]);

  const entries = gloss?.entries || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries
      .filter(e => {
        if (categoryFilter && (e.category || "") !== categoryFilter) return false;
        if (q) {
          const hay = `${e.french} ${e.english} ${e.notes || ""} ${e.category || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .slice()
      .sort((a, b) => {
        if (sortKey === "english") return (a.english || "").localeCompare(b.english || "", undefined, { sensitivity: "base" });
        if (sortKey === "category") {
          const ac = (a.category || "zzz"); const bc = (b.category || "zzz");
          const comp = ac.localeCompare(bc);
          if (comp !== 0) return comp;
          return (a.french || "").localeCompare(b.french || "", "fr", { sensitivity: "base" });
        }
        return (a.french || "").localeCompare(b.french || "", "fr", { sensitivity: "base" });
      });
  }, [entries, search, categoryFilter, sortKey]);

  const openAdd = () => { setEditingEntry(null); setModalOpen(true); };
  const openEdit = (e) => { setEditingEntry(e); setModalOpen(true); };

  const onSave = async (body) => {
    if (editingEntry) {
      await apiUpdateGlossaryEntry(deptId, editingEntry.id, body);
    } else {
      await apiAddGlossaryEntry(deptId, body);
    }
    setModalOpen(false);
    reload();
  };

  const onDelete = async (entry) => {
    await apiDeleteGlossaryEntry(deptId, entry.id);
    setModalOpen(false);
    reload();
  };

  const print = () => {
    openGlossaryPrint({
      entries: filtered.length > 0 ? filtered : entries,
      lastUpdatedAt: gloss?.lastUpdatedAt,
      lastUpdatedByName: gloss?.lastUpdatedByName,
    });
  };

  const stampedAt = gloss?.lastUpdatedAt ? new Date(gloss.lastUpdatedAt).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;

  return (
    <div data-testid="translation-glossary-tab">
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "'Cinzel',serif", fontSize: 20, margin: 0, letterSpacing: 1 }}>
          TRANSLATION GLOSSARY
        </h2>
        {stampedAt && (
          <span style={{ fontSize: 11, opacity: 0.6 }} data-testid="translation-glossary-stamp">
            Last updated {stampedAt}{gloss?.lastUpdatedByName ? " · " + gloss.lastUpdatedByName : ""}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={print}
          data-testid="translation-glossary-print"
          disabled={entries.length === 0}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: entries.length === 0 ? 0.5 : 1 }}
        >
          <CustomIcon name="document" size={12} /> Download PDF
        </button>
        {canEdit && (
          <button
            type="button"
            className="btn"
            onClick={openAdd}
            data-testid="translation-glossary-add"
            style={{
              background: "var(--kc-accent)", color: "#111", fontWeight: 800,
              padding: "0 18px", minHeight: 38, borderRadius: 8,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          ><span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Entry</button>
        )}
      </div>

      {/* FILTERS */}
      <div
        style={{
          display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
          padding: 12,
          background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)",
          borderRadius: 10, marginBottom: 14,
        }}
      >
        <div style={{ flex: "1 1 220px", minWidth: 180 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Search</label>
          <input
            type="search"
            className="form-input"
            value={search}
            placeholder="French or English term…"
            onChange={(e) => setSearch(e.target.value)}
            data-testid="translation-glossary-search"
          />
        </div>
        <div style={{ flex: "0 0 180px" }}>
          <label className="form-label" style={{ fontSize: 11 }}>Category</label>
          <select
            className="form-input"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="translation-glossary-filter-category"
          >
            <option value="">All categories</option>
            {GLOSSARY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ flex: "0 0 180px" }}>
          <label className="form-label" style={{ fontSize: 11 }}>Sort by</label>
          <select
            className="form-input"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            data-testid="translation-glossary-sort"
          >
            {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <div style={{ padding: 20, textAlign: "center", opacity: 0.6 }} data-testid="translation-glossary-loading">Loading…</div>
      ) : error ? (
        <div style={{ padding: 12, background: "rgba(200,40,40,0.15)", color: "#f2a2a2", borderRadius: 8 }} data-testid="translation-glossary-error">{error}</div>
      ) : filtered.length === 0 ? (
        <div
          style={{ padding: 40, textAlign: "center", opacity: 0.6, border: "1px dashed color-mix(in srgb, var(--kc-accent) 25%, transparent)", borderRadius: 10 }}
          data-testid="translation-glossary-empty"
        >
          {entries.length === 0 ? "The glossary is empty." : "No entries match these filters."}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
          {filtered.map(e => (
            <article
              key={e.id}
              data-testid={`translation-glossary-card-${e.id}`}
              onClick={canEdit ? () => openEdit(e) : undefined}
              style={{
                padding: 14,
                background: "var(--kc-surface)",
                border: "1px solid var(--kc-border, rgba(255,255,255,0.08))",
                borderLeft: "4px solid #3b82f6",
                borderRadius: 10,
                cursor: canEdit ? "pointer" : "default",
                display: "flex", flexDirection: "column", gap: 6,
              }}
            >
              {e.category && (
                <div style={{
                  fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6,
                  color: "var(--kc-accent)", fontWeight: 700,
                }}>{e.category}</div>
              )}
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--kc-text)" }} lang="fr">
                  {e.french}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "var(--kc-accent)" }} lang="en">
                  {e.english}
                </div>
              </div>
              {e.notes && (
                <div style={{ fontSize: 12, opacity: 0.75, lineHeight: 1.4 }}>{e.notes}</div>
              )}
            </article>
          ))}
        </div>
      )}

      <GlossaryEntryModal
        open={modalOpen}
        entry={editingEntry}
        onSave={onSave}
        onDelete={editingEntry ? onDelete : null}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
